import { type RefObject, useEffect, useRef } from 'react';
import { clamp } from 'reactApp/utils/mathHelpers';

function calcPosition(node: HTMLElement, x: number, y: number) {
    const { width, height, left, top } = node.getBoundingClientRect();
    if (width && height) {
        return {
            x: clamp((x - left) / width, 0, 1),
            y: clamp((y - top) / height, 0, 1),
        };
    }
    return null;
}

export function useMove<T extends HTMLElement = any>(
    onChange: (value: { x: number; y: number }) => void,
    thumbRef?: RefObject<HTMLDivElement>,
    thumbSatellite?: string
) {
    const ref = useRef<T>(null);
    const thumbOffsetRef = useRef<{ offsetX: number; offsetY: number }>({ offsetX: 0, offsetY: 0 });
    const mounted = useRef<boolean>(false);
    const isMoving = useRef(false);
    const animationFrame = useRef(0);

    useEffect(() => {
        mounted.current = true;
    }, []);

    useEffect(() => {
        const node = ref.current;

        const onMoving = ({ x, y }: { x: number; y: number }) => {
            cancelAnimationFrame(animationFrame.current);

            animationFrame.current = requestAnimationFrame(() => {
                if (mounted.current && node) {
                    node.style.userSelect = 'none';
                    const newPosition = calcPosition(node, x, y);
                    if (newPosition) {
                        onChange(newPosition);
                    }
                }
            });
        };

        const subscribe = () => {
            document.addEventListener('mousemove', onMouseMove);
            document.addEventListener('mouseup', onMoveEnd);
            document.addEventListener('touchmove', onTouchMove);
            document.addEventListener('touchend', onMoveEnd);
        };

        const unsubscribe = () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMoveEnd);
            document.removeEventListener('touchmove', onTouchMove);
            document.removeEventListener('touchend', onMoveEnd);
        };

        const onStartMove = () => {
            if (!isMoving.current && mounted.current) {
                isMoving.current = true;
                subscribe();
            }
        };

        const onMoveEnd = () => {
            if (isMoving.current && mounted.current) {
                isMoving.current = false;
                unsubscribe();
            }
        };

        const initThumbOffset = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (target?.closest?.(`#${thumbSatellite}`)) {
                const { x, y, width, height } = thumbRef?.current?.getBoundingClientRect() || { x: 0, y: 0, height: 0, width: 0 };
                const posX = x + width / 2;
                const posY = y + height / 2;
                thumbOffsetRef.current.offsetX = event.clientX - posX;
                thumbOffsetRef.current.offsetY = event.clientY - posY;
            } else {
                thumbOffsetRef.current.offsetX = 0;
                thumbOffsetRef.current.offsetY = 0;
            }
        };

        const onMouseDown = (event: MouseEvent) => {
            initThumbOffset(event);
            onStartMove();
            event.preventDefault();
            onMouseMove(event);
        };

        const calcThumbOffsetPosition = (clientX: number, clientY: number) => {
            return { x: clientX - thumbOffsetRef.current.offsetX, y: clientY - thumbOffsetRef.current.offsetY };
        };

        const onMouseMove = (event: MouseEvent) => onMoving(calcThumbOffsetPosition(event.clientX, event.clientY));

        const preventTouchDefault = (event: TouchEvent) => {
            if (event.cancelable) {
                event.preventDefault();
            }
        };

        const onTouchStart = (event: TouchEvent) => {
            preventTouchDefault(event);
            onStartMove();
            onTouchMove(event);
        };

        const onTouchMove = (event: TouchEvent) => {
            preventTouchDefault(event);
            onMoving(calcThumbOffsetPosition(event.changedTouches[0].clientX, event.changedTouches[0].clientY));
        };

        if (node) {
            node.addEventListener('mousedown', onMouseDown);
            node.addEventListener('touchstart', onTouchStart, { passive: false });
        }

        return () => {
            if (node) {
                node.removeEventListener('mousedown', onMouseDown);
                node.removeEventListener('touchstart', onTouchStart);
            }
        };
    }, [onChange, thumbSatellite]);

    return { ref };
}
